export interface DataPoint {
  label: string;
  value: number;
}

export interface ChartDimensions {
  width: number;
  height: number;
  padding: { top: number; right: number; bottom: number; left: number };
}

export interface PlotArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function getPlotArea(dimensions: ChartDimensions): PlotArea {
  const { width, height, padding } = dimensions;
  return {
    x: padding.left,
    y: padding.top,
    width: width - padding.left - padding.right,
    height: height - padding.top - padding.bottom,
  };
}

export function linearScale(
  value: number,
  domainMin: number,
  domainMax: number,
  rangeMin: number,
  rangeMax: number,
): number {
  const domain = domainMax - domainMin;
  if (domain === 0) return (rangeMin + rangeMax) / 2;
  return rangeMin + ((value - domainMin) / domain) * (rangeMax - rangeMin);
}

export function getDataRange(data: DataPoint[]): { min: number; max: number } {
  if (data.length === 0) return { min: 0, max: 0 };
  let min = data[0].value;
  let max = data[0].value;
  for (let i = 1; i < data.length; i++) {
    if (data[i].value < min) min = data[i].value;
    if (data[i].value > max) max = data[i].value;
  }
  return { min, max };
}

export function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  if (normalized <= 1) return magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 5) return 5 * magnitude;
  return 10 * magnitude;
}

export function generateTicks(min: number, max: number, count: number): number[] {
  if (max <= min) return [min];
  const range = max - min;
  const step = range / (count - 1);
  const ticks: number[] = [];
  for (let i = 0; i < count; i++) {
    ticks.push(min + step * i);
  }
  return ticks;
}

export function formatTickLabel(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(1);
}

export function generateUniqueId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).substring(2, 11)}`;
}
